import {
    computed,
    inject,
    Injectable,
    linkedSignal,
    signal,
} from '@angular/core';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import {
    FILE_PREVIEW_PANEL_SIDE,
    type FilePreviewPanelConfig,
} from '../models';

export const DOCUMENT_INFO_PANEL_ID = 'document-information';

export interface PreviewPanelStateInput {
    previewUrl: string | null | undefined;
    fileName: string;
    panels?: readonly FilePreviewPanelConfig[] | undefined;
}

interface PreviewPanelStateParams {
    previewUrl: string | null | undefined;
    fileName: string;
    initialLeftPanelId: string | null;
    initialRightPanelId: string;
    enabledLeftPanelIds: readonly string[];
    enabledRightPanelIds: readonly string[];
    retainedPanelIds: readonly string[];
    singlePaneBelowDesktop: boolean;
}

@Injectable()
export class PreviewPanelState {
    private readonly viewport = injectViewportBreakpoints();
    private readonly belowTablet = this.viewport.lessThan('tablet768');
    public readonly singlePane = computed(() =>
        this.params().singlePaneBelowDesktop
            ? !this.viewport.isDesktop()
            : this.belowTablet()
    );

    //#region State
    private readonly params = signal<PreviewPanelStateParams>({
        previewUrl: undefined,
        fileName: '',
        initialLeftPanelId: null,
        initialRightPanelId: DOCUMENT_INFO_PANEL_ID,
        enabledLeftPanelIds: [],
        enabledRightPanelIds: [],
        retainedPanelIds: [],
        singlePaneBelowDesktop: false,
    });
    public readonly controlsVisible = linkedSignal<
        Pick<PreviewPanelStateInput, 'previewUrl' | 'fileName'>,
        boolean
    >({
        source: () => ({
            previewUrl: this.params().previewUrl,
            fileName: this.params().fileName,
        }),
        computation: (source, previous) =>
            previous &&
            previous.source.previewUrl === source.previewUrl &&
            previous.source.fileName === source.fileName
                ? previous.value
                : true,
    });
    public readonly activeLeftPanelId = linkedSignal({
        source: () => this.params(),
        computation: (params, previous): string | null =>
            this.reconcileActivePanelId(
                params,
                previous,
                FILE_PREVIEW_PANEL_SIDE.Left
            ),
    });
    public readonly activeRightPanelId = linkedSignal({
        source: () => ({
            params: this.params(),
            singlePane: this.singlePane(),
            leftPanelId: this.singlePane() ? this.activeLeftPanelId() : null,
        }),
        computation: (source, previous): string | null => {
            if (source.singlePane && source.leftPanelId !== null) return null;
            return this.reconcileActivePanelId(
                source.params,
                previous
                    ? { source: previous.source.params, value: previous.value }
                    : undefined,
                FILE_PREVIEW_PANEL_SIDE.Right
            );
        },
    });
    //#endregion

    //#region Public methods
    public setParams(params: PreviewPanelStateInput): void {
        const initialLeftPanelId = this.resolveInitialPanelId(
            params.panels ?? [],
            FILE_PREVIEW_PANEL_SIDE.Left
        );
        const initialRightPanelId =
            this.resolveInitialPanelId(
                params.panels ?? [],
                FILE_PREVIEW_PANEL_SIDE.Right
            ) ?? DOCUMENT_INFO_PANEL_ID;
        const enabledLeftPanelIds = this.resolveEnabledPanelIds(
            params.panels ?? [],
            FILE_PREVIEW_PANEL_SIDE.Left
        );
        const enabledRightPanelIds = this.resolveEnabledPanelIds(
            params.panels ?? [],
            FILE_PREVIEW_PANEL_SIDE.Right
        );
        const retainedPanelIds = (params.panels ?? [])
            .filter((panel) => panel.retainOnSourceChange && !panel.disabled)
            .map((panel) => `${panel.side}:${panel.id}`);
        const singlePaneBelowDesktop = (params.panels ?? []).some(
            (panel) => !panel.disabled && panel.singlePaneBelow === 'desktop'
        );
        const current = this.params();
        if (
            current.singlePaneBelowDesktop === singlePaneBelowDesktop &&
            this.haveSameIds(current.retainedPanelIds, retainedPanelIds) &&
            current.previewUrl === params.previewUrl &&
            current.fileName === params.fileName &&
            current.initialLeftPanelId === initialLeftPanelId &&
            current.initialRightPanelId === initialRightPanelId &&
            this.haveSameIds(
                current.enabledLeftPanelIds,
                enabledLeftPanelIds
            ) &&
            this.haveSameIds(current.enabledRightPanelIds, enabledRightPanelIds)
        ) {
            return;
        }

        this.params.set({
            previewUrl: params.previewUrl,
            fileName: params.fileName,
            initialLeftPanelId,
            initialRightPanelId,
            enabledLeftPanelIds,
            enabledRightPanelIds,
            retainedPanelIds,
            singlePaneBelowDesktop,
        });
    }

    public setControlsVisible(visible: boolean): void {
        this.controlsVisible.set(visible);
    }

    public togglePanel(panel: FilePreviewPanelConfig): void {
        if (panel.disabled) return;

        const activePanelId =
            panel.side === FILE_PREVIEW_PANEL_SIDE.Left
                ? this.activeLeftPanelId
                : this.activeRightPanelId;

        if (this.singlePane() && activePanelId() !== panel.id) {
            if (panel.side === FILE_PREVIEW_PANEL_SIDE.Left) {
                this.activeRightPanelId.set(null);
            } else {
                this.activeLeftPanelId.set(null);
            }
        }
        activePanelId.update((currentId) =>
            currentId === panel.id ? null : panel.id
        );
    }

    public toggleDocumentInfo(): void {
        if (
            this.singlePane() &&
            this.activeRightPanelId() !== DOCUMENT_INFO_PANEL_ID
        ) {
            this.activeLeftPanelId.set(null);
        }
        this.activeRightPanelId.update((currentId) =>
            currentId === DOCUMENT_INFO_PANEL_ID ? null : DOCUMENT_INFO_PANEL_ID
        );
    }

    public closeLeftPanel(): void {
        this.activeLeftPanelId.set(null);
    }

    public closeAllPanels(): void {
        this.activeLeftPanelId.set(null);
        this.activeRightPanelId.set(null);
    }
    //#endregion

    //#region Private methods
    private resolveInitialPanelId(
        panels: readonly FilePreviewPanelConfig[],
        side: FilePreviewPanelConfig['side']
    ): string | null {
        return (
            panels.find(
                (panel) =>
                    panel.side === side &&
                    panel.initiallyActive === true &&
                    !panel.disabled
            )?.id ?? null
        );
    }

    private resolveEnabledPanelIds(
        panels: readonly FilePreviewPanelConfig[],
        side: FilePreviewPanelConfig['side']
    ): readonly string[] {
        return panels
            .filter((panel) => panel.side === side && !panel.disabled)
            .map((panel) => panel.id);
    }

    private reconcileActivePanelId(
        params: PreviewPanelStateParams,
        previous:
            | {
                  source: PreviewPanelStateParams;
                  value: string | null;
              }
            | undefined,
        side: FilePreviewPanelConfig['side']
    ): string | null {
        const fallbackId =
            side === FILE_PREVIEW_PANEL_SIDE.Left
                ? params.initialLeftPanelId
                : params.initialRightPanelId;
        if (
            !previous ||
            previous.source.previewUrl !== params.previewUrl ||
            previous.source.fileName !== params.fileName
        ) {
            if (previous) {
                const activeId = previous.value;
                if (
                    activeId !== null &&
                    params.retainedPanelIds.includes(`${side}:${activeId}`)
                ) {
                    return activeId;
                }
                if (
                    activeId === null &&
                    params.retainedPanelIds.some(
                        (id) =>
                            id.startsWith(`${side}:`) &&
                            previous.source.retainedPanelIds.includes(id)
                    )
                ) {
                    return null;
                }
            }
            return fallbackId;
        }

        const activeId = previous.value;
        if (activeId === null) {
            const previousFallbackId =
                side === FILE_PREVIEW_PANEL_SIDE.Left
                    ? previous.source.initialLeftPanelId
                    : previous.source.initialRightPanelId;

            return previousFallbackId === null ? fallbackId : null;
        }

        if (
            side === FILE_PREVIEW_PANEL_SIDE.Right &&
            activeId === DOCUMENT_INFO_PANEL_ID
        ) {
            return previous.source.initialRightPanelId ===
                DOCUMENT_INFO_PANEL_ID && fallbackId !== DOCUMENT_INFO_PANEL_ID
                ? fallbackId
                : activeId;
        }

        const enabledIds =
            side === FILE_PREVIEW_PANEL_SIDE.Left
                ? params.enabledLeftPanelIds
                : params.enabledRightPanelIds;

        return enabledIds.includes(activeId) ? activeId : fallbackId;
    }

    private haveSameIds(
        current: readonly string[],
        next: readonly string[]
    ): boolean {
        return (
            current.length === next.length &&
            current.every((id, index) => id === next[index])
        );
    }
    //#endregion
}

export const PREVIEW_PANEL_STATE_PROVIDER = {
    provide: PreviewPanelState,
    useFactory: (): PreviewPanelState =>
        inject(PreviewPanelState, { optional: true, skipSelf: true }) ??
        new PreviewPanelState(),
};
